import React, { useState, useEffect } from 'react';
import './AddSaleModal.css';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonSpinner,
  IonText,
  IonIcon,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonSegment,
  IonSegmentButton,
  IonTextarea,
  IonNote,
  IonBadge,
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  closeCircleOutline,
  closeCircle,
  locationOutline,
  personOutline,
  pricetagOutline,
  ticketOutline,
  documentTextOutline,
  walletOutline,
  homeOutline,
  informationCircleOutline,
  callOutline,
  mailOutline,
  cashOutline,
  cardOutline,
  swapHorizontalOutline,
  ellipsisHorizontalOutline,
  layersOutline,
  gridOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  saleService,
  ticketService,
  stadiumService,
  supplierService,
} from '../services/api';
import { Event, Ticket, Sale, Supplier } from '../types';
import {
  validateEmail,
  validatePhone,
  validatePrice,
  validateQuantity,
  validateName,
  getValidationError,
} from '../utils/validation';
import InlineSupplierForm from './InlineSupplierForm';
import SupplierDetailsCard from './SupplierDetailsCard';
import {
  RESTRICTION_OPTIONS,
  SPLIT_TYPE_OPTIONS,
  calculateSplitGroups,
} from '../utils/ticketConstants';
import {
  CURRENCIES,
  DEFAULT_CURRENCY,
  getCurrencySymbol,
} from '../utils/currencyConstants';
import { eventBus, EVENTS } from '../utils/eventBus';

interface AddSaleModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  event: Event | null;
  onSuccess: () => void;
  onBeforeSubmit?: () => Promise<Event | null>;
  sale?: Sale | null; // Optional sale prop for edit mode
  inventoryItem?: Ticket | null; // Optional inventory item for selling from inventory
}

interface FormData {
  saleType: 'private' | 'supplier';
  supplierId: string;
  newSupplierName: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  salePrice: number;
  currency: string;
  paymentMethod: 'cash' | 'card' | 'bankTransfer' | 'other';
  selectedTickets: string[];
  quantity: number;
  notes: string;
  section: string;
  category: string;
  block: string;
  row: string;
  seat: string;
  restrictions: string[];
  customRestriction: string;
  splitType: string;
}

const AddSaleModal: React.FC<AddSaleModalProps> = ({
  isOpen,
  onDismiss,
  event,
  onSuccess,
  onBeforeSubmit,
  sale,
  inventoryItem,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!sale;
  const [activeTab, setActiveTab] = useState<'tickets' | 'direct'>('direct');

  const getInitialFormData = (): FormData => {
    if (isEditMode && sale) {
      const restrictionsList = sale.restrictions
        ? sale.restrictions
            .split(',')
            .map((r) => r.trim())
            .filter((r) => {
              return (
                RESTRICTION_OPTIONS.some((opt) => opt.id === r) ||
                !RESTRICTION_OPTIONS.some((opt) => opt.label === r)
              );
            })
        : [];

      return {
        saleType: sale.saleType || 'private',
        supplierId: sale.supplierId || '',
        newSupplierName: '',
        buyerName: sale.buyerName || '',
        buyerEmail: sale.buyerEmail || '',
        buyerPhone: sale.buyerPhone || '',
        salePrice: sale.salePrice || 0,
        currency: sale.currency || DEFAULT_CURRENCY,
        paymentMethod: sale.paymentMethod || 'cash',
        selectedTickets: sale.ticketIds || [],
        quantity: sale.quantity || 1,
        notes: sale.notes || '',
        section: sale.section || '',
        category: sale.category || '',
        block: sale.block || '',
        row: sale.row || '',
        seat: sale.seat || '',
        restrictions: restrictionsList,
        customRestriction: '',
        splitType: sale.split || '',
      };
    }

    // Pre-populate from inventory item if selling from inventory
    if (inventoryItem) {
      const availableQty =
        inventoryItem.quantity - (inventoryItem.soldQuantity || 0);

      // Parse restrictions from string to array
      const restrictionsList = inventoryItem.restrictions
        ? inventoryItem.restrictions
            .split(',')
            .map((r) => r.trim())
            .filter((r) => {
              return (
                RESTRICTION_OPTIONS.some((opt) => opt.id === r) ||
                !RESTRICTION_OPTIONS.some((opt) => opt.label === r)
              );
            })
        : [];

      return {
        saleType:
          inventoryItem.sourceType === 'supplier' ? 'supplier' : 'private',
        supplierId:
          inventoryItem.sourceType === 'supplier' &&
          typeof inventoryItem.supplierId === 'string'
            ? inventoryItem.supplierId
            : '',
        newSupplierName: '',
        buyerName: '',
        buyerEmail: '',
        buyerPhone: '',
        salePrice: inventoryItem.wantedPrice || 0,
        currency: inventoryItem.currency || DEFAULT_CURRENCY,
        paymentMethod: 'cash',
        selectedTickets: inventoryItem._id ? [inventoryItem._id] : [],
        quantity: availableQty, // Default to all available quantity
        notes: '',
        section: inventoryItem.section || '',
        category: inventoryItem.category || '',
        block: inventoryItem.block || '',
        row: inventoryItem.row || '',
        seat: inventoryItem.seat || '',
        restrictions: restrictionsList,
        customRestriction: '',
        splitType: inventoryItem.split || '',
      };
    }

    return {
      saleType: 'supplier',
      supplierId: '',
      newSupplierName: '',
      buyerName: '',
      buyerEmail: '',
      buyerPhone: '',
      salePrice: 0,
      currency: DEFAULT_CURRENCY,
      paymentMethod: 'cash',
      selectedTickets: [],
      quantity: 1,
      notes: '',
      section: '',
      category: '',
      block: '',
      row: '',
      seat: '',
      restrictions: [],
      customRestriction: '',
      splitType: '',
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stadiumData, setStadiumData] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [filteredBlocks, setFilteredBlocks] = useState<any[]>([]);
  const [loadingStadium, setLoadingStadium] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [defaultSupplier, setDefaultSupplier] = useState<Supplier | null>(null);
  const [showNewSupplierInput, setShowNewSupplierInput] = useState(false);
  const [showInlineSupplierForm, setShowInlineSupplierForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [loadingSupplier, setLoadingSupplier] = useState(false);

  // Track if we've already mapped categories to prevent re-initialization
  const [categoriesMapped, setCategoriesMapped] = useState(false);

  // Don't automatically set default supplier - user must choose
  // useEffect(() => {
  //   if (
  //     defaultSupplier &&
  //     !formData.supplierId &&
  //     formData.saleType === 'supplier'
  //   ) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       supplierId: defaultSupplier._id || defaultSupplier.id || '',
  //     }));
  //     setSelectedSupplier(defaultSupplier);
  //   }
  // }, [defaultSupplier]);

  // Update form data when modal opens or props change
  useEffect(() => {
    if (event && isOpen) {
      const newFormData = getInitialFormData();
      setFormData(newFormData);
      setCategoriesMapped(false); // Reset mapping flag
      // Reset stadium data when opening modal
      setCategories([]);
      setBlocks([]);
      setFilteredBlocks([]);
      setStadiumData(null);

      // Set active tab based on whether sale has tickets or if selling from inventory
      if (isEditMode && sale) {
        setActiveTab(
          sale.ticketIds && sale.ticketIds.length > 0 ? 'tickets' : 'direct'
        );
      } else if (inventoryItem) {
        // If selling from inventory, use tickets tab to show the linked inventory
        setActiveTab('tickets');
      } else {
        setActiveTab('direct');
      }
    }
  }, [isOpen, event?._id, inventoryItem?._id]);

  // Load data when modal opens
  useEffect(() => {
    if (event && isOpen) {
      loadAvailableTickets();
      loadStadiumData(event.venue);
      loadSuppliers();
    }
  }, [event?.venue, isOpen]);

  // Update category and block values when stadium data loads in edit mode
  useEffect(() => {
    if (isEditMode && sale && categories.length > 0 && !categoriesMapped) {
      let updates: any = {};

      // Check if we need to map category name to ID
      // Use sale.category directly instead of formData.category since formData might be empty
      if (sale.category) {
        // Check if sale.category is already an ID
        const isAlreadyId = categories.some((c) => c.id === sale.category);

        if (!isAlreadyId) {
          // It's a name, find the matching category by name (case-insensitive)
          const matchingCategory = categories.find(
            (c) =>
              c.name.trim().toLowerCase() ===
              sale.category?.trim().toLowerCase()
          );
          if (matchingCategory) {
            updates.category = matchingCategory.id;
          }
        } else {
          // It's already an ID, just set it
          updates.category = sale.category;
        }
      }

      // Check blocks after we have categories set
      if (sale.block && blocks.length > 0) {
        // Check if sale.block is already an ID
        const isAlreadyId = blocks.some((b) => b.id === sale.block);

        if (!isAlreadyId) {
          // It's a name, find the matching block by name (case-insensitive)
          const matchingBlock = blocks.find(
            (b) =>
              b.name.trim().toLowerCase() === sale.block?.trim().toLowerCase()
          );
          if (matchingBlock) {
            updates.block = matchingBlock.id;
          }
        } else {
          // It's already an ID, just set it
          updates.block = sale.block;
        }
      }

      // Apply all updates at once
      if (Object.keys(updates).length > 0) {
        setFormData((prev) => ({ ...prev, ...updates }));
        setCategoriesMapped(true);
      }
    }
  }, [categories.length, blocks.length, isEditMode, sale, categoriesMapped]);

  const loadAvailableTickets = async () => {
    if (!event) return;

    setLoadingTickets(true);
    try {
      const tickets = await ticketService.getByEvent(event._id);
      const available = tickets.filter(
        (t: Ticket) => t.status === 'available' && t.quantity > 0
      );
      setAvailableTickets(available);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadStadiumData = async (venue: string) => {
    console.log(`Loading stadium data for: ${venue}`);
    setLoadingStadium(true);

    // Always reset to defaults first
    setStadiumData(null);
    setBlocks([]);
    setFilteredBlocks([]);

    try {
      const data = await stadiumService.getStadiumData(venue);
      console.log('Stadium data received:', data);

      if (data && data.categories && data.categories.length > 0) {
        // Stadium exists with categories
        console.log('Using stadium categories:', data.categories);
        setStadiumData(data);
        setCategories(data.categories);
        setBlocks(data.blocks || []);
        setFilteredBlocks(data.blocks || []);
      } else {
        // Stadium exists but has no categories OR no data returned
        console.log('No categories found, using defaults');
        setCategories([
          { id: 'shortside', name: 'Shortside' },
          { id: 'longside', name: 'Longside' },
          { id: 'away', name: 'Away' },
        ]);
      }
    } catch (error: any) {
      console.log(
        `Stadium data not found for ${venue}, using default categories`
      );
      // Set default categories when stadium doesn't exist
      setCategories([
        { id: 'shortside', name: 'Shortside' },
        { id: 'longside', name: 'Longside' },
        { id: 'away', name: 'Away' },
      ]);
    } finally {
      setLoadingStadium(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getAllWithBalance();
      setSuppliers(data || []);

      // Set default supplier (first one if available)
      if (data && data.length > 0) {
        const firstSupplier = data[0];
        setDefaultSupplier(firstSupplier);
      }
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      setSuppliers([]);
    }
  };

  // Filter blocks based on selected category
  useEffect(() => {
    if (formData.category && blocks.length > 0) {
      const filtered = blocks.filter((block) =>
        block.categories.includes(formData.category)
      );
      setFilteredBlocks(filtered);
    } else {
      setFilteredBlocks(blocks);
    }
  }, [formData.category, blocks]);

  // Set default supplier when suppliers are loaded and saleType is supplier
  useEffect(() => {
    if (
      suppliers.length > 0 &&
      formData.saleType === 'supplier' &&
      !formData.supplierId &&
      !isEditMode
    ) {
      // If we're creating sale from inventory and it has a supplier ID, use it
      if (inventoryItem?.supplierId) {
        const inventorySupplier = suppliers.find(
          (s) => (s._id || s.id) === inventoryItem.supplierId
        );
        if (inventorySupplier) {
          const supplierId =
            inventorySupplier._id || inventorySupplier.id || '';
          setFormData((prev) => ({ ...prev, supplierId }));
          setSelectedSupplier(inventorySupplier);
          return;
        }
      }

      // Don't set any default supplier - user must choose
      // The supplier dropdown will show "Select Supplier" as placeholder
    }
  }, [
    suppliers,
    formData.saleType,
    formData.supplierId,
    isEditMode,
    inventoryItem?.supplierId,
  ]);

  const calculateTotalCost = () => {
    const selectedTicket = availableTickets.find((ticket) =>
      formData.selectedTickets.includes(ticket.id || ticket._id || '')
    );
    if (!selectedTicket) return 0;
    return selectedTicket.purchasePrice * formData.quantity;
  };

  const calculateProfit = () => {
    const totalCost = calculateTotalCost();
    const totalRevenue = formData.salePrice * formData.quantity;
    return totalRevenue - totalCost;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate supplier selection for supplier sales
    if (formData.saleType === 'supplier' && !formData.supplierId) {
      newErrors.supplierId = 'Supplier selection is required';
    }

    // Validate buyer info
    if (formData.buyerName && !validateName(formData.buyerName)) {
      newErrors.buyerName =
        getValidationError('buyerName', formData.buyerName) || '';
    }

    if (formData.buyerEmail && !validateEmail(formData.buyerEmail)) {
      newErrors.buyerEmail =
        getValidationError('email', formData.buyerEmail) || '';
    }

    if (formData.buyerPhone && !validatePhone(formData.buyerPhone)) {
      newErrors.buyerPhone =
        getValidationError('phone', formData.buyerPhone) || '';
    }

    // Validate sale price
    if (!validatePrice(formData.salePrice)) {
      newErrors.salePrice =
        getValidationError('salePrice', formData.salePrice) || '';
    }

    // Validate quantity for direct sales
    if (activeTab === 'direct' && !validateQuantity(formData.quantity)) {
      newErrors.quantity =
        getValidationError('quantity', formData.quantity) || '';
    }

    // Validate ticket selection
    if (activeTab === 'tickets' && formData.selectedTickets.length === 0) {
      newErrors.selectedTickets = 'Please select at least one ticket';
    }

    // Validate quantity for ticket sales
    if (activeTab === 'tickets' && formData.selectedTickets.length > 0) {
      const selectedTicket = availableTickets.find(
        (t) => (t.id || t._id) === formData.selectedTickets[0]
      );
      if (selectedTicket) {
        const maxAvailable =
          selectedTicket.quantity - (selectedTicket.soldQuantity || 0);
        if (formData.quantity < 1 || formData.quantity > maxAvailable) {
          newErrors.quantity = `Quantity must be between 1 and ${maxAvailable}`;
        }
      }
    }

    // Validate category (required)
    if (!formData.category || formData.category.trim() === '') {
      newErrors.category = 'Category is required';
    }

    // Validate split type (required)
    if (!formData.splitType || formData.splitType.trim() === '') {
      newErrors.splitType = 'Split type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    // Basic validation without setting errors (for button state)
    if (formData.salePrice <= 0) return false;
    if (!formData.category || formData.category.trim() === '') return false;
    if (!formData.splitType || formData.splitType.trim() === '') return false;

    // Validate supplier selection for supplier sales
    if (formData.saleType === 'supplier' && !formData.supplierId) return false;

    if (activeTab === 'tickets' && formData.selectedTickets.length === 0)
      return false;
    if (activeTab === 'direct' && formData.quantity < 1) return false;

    return true;
  };

  const handleSubmit = async () => {
    if (!event || !isFormValid() || !validateForm()) return;

    setSubmitting(true);
    try {
      // If the event is temporary, create the real event first
      let actualEvent = event;
      if (event._id.startsWith('temp-') && onBeforeSubmit && !isEditMode) {
        const createdEvent = await onBeforeSubmit();
        if (!createdEvent) {
          // Event creation failed
          setSubmitting(false);
          return;
        }
        actualEvent = createdEvent;
      }

      const saleData: any = {
        buyerName: formData.buyerName,
        salePrice: formData.salePrice,
        currency: formData.currency,
        paymentMethod: formData.paymentMethod,
        saleDate: new Date().toISOString(),
        saleType: formData.saleType,
      };

      // Handle supplier logic
      if (formData.saleType === 'supplier') {
        if (formData.supplierId === 'new' && formData.newSupplierName.trim()) {
          // Create new supplier
          try {
            const newSupplier = await supplierService.create({
              name: formData.newSupplierName.trim(),
            });
            saleData.supplierId = newSupplier._id || newSupplier.id;
          } catch (error) {
            console.error('Failed to create supplier:', error);
            // Continue without supplier ID
          }
        } else if (formData.supplierId && formData.supplierId !== 'new') {
          saleData.supplierId = formData.supplierId;
        }
      }

      // Only include optional fields if they have values
      if (formData.buyerEmail && formData.buyerEmail.trim()) {
        saleData.buyerEmail = formData.buyerEmail.trim();
      }
      if (formData.buyerPhone && formData.buyerPhone.trim()) {
        saleData.buyerPhone = formData.buyerPhone.trim();
      }
      if (formData.row && formData.row.trim()) {
        saleData.row = formData.row.trim();
      }
      if (formData.seat && formData.seat.trim()) {
        saleData.seat = formData.seat.trim();
      }
      if (formData.restrictions.length > 0) {
        saleData.restrictions = formData.restrictions.join(', ');
      }
      if (formData.splitType) {
        saleData.split = formData.splitType;
      }

      // Include category and block information
      if (formData.category) {
        // Find the category name from the ID
        const selectedCategory = categories.find(
          (c) => c.id === formData.category
        );
        if (selectedCategory) {
          saleData.category = selectedCategory.name; // Use name instead of ID
        } else {
          // If no category found (manual input), use the value as is
          saleData.category = formData.category;
        }
      }
      // Always include block field to handle clearing
      if (formData.block && formData.block !== '') {
        // Find the block name from the ID
        const selectedBlock = blocks.find((b) => b.id === formData.block);
        if (selectedBlock) {
          saleData.block = selectedBlock.name; // Use name instead of ID
          saleData.section = selectedBlock.name; // For backward compatibility
        } else {
          // If no block found (manual input), use the value as is
          saleData.block = formData.block;
          saleData.section = formData.block;
        }
      } else {
        // Explicitly set block to empty string to clear it
        saleData.block = '';

        if (formData.category) {
          // If only category is selected, use category name as section
          const selectedCategory = categories.find(
            (c) => c.id === formData.category
          );
          if (selectedCategory) {
            saleData.section = selectedCategory.name;
          } else {
            saleData.section = formData.category;
          }
        }
      }

      // Always include eventId
      saleData.eventId = actualEvent._id;

      // If tickets are selected, include them
      if (activeTab === 'tickets' && formData.selectedTickets.length > 0) {
        saleData.ticketIds = formData.selectedTickets;
        // Include quantity for inventory sales
        saleData.quantity = formData.quantity || 1;
        saleData.notes =
          formData.notes ||
          `Inventory sale - Quantity: ${formData.quantity || 1}`;
      } else {
        // Otherwise, create a sale without tickets but with quantity
        saleData.quantity = formData.quantity;
        saleData.notes =
          formData.notes || `Direct sale - Quantity: ${formData.quantity}`;
      }

      if (isEditMode && sale) {
        // Update existing sale
        await saleService.update(sale.id || sale._id || '', saleData);
      } else {
        // Create new sale
        await saleService.create(saleData);

        // Emit event to refresh inventory if this was a sale from inventory
        if (activeTab === 'tickets' && formData.selectedTickets.length > 0) {
          eventBus.emit(EVENTS.REFRESH_TICKETS);
        }

        // Always emit refresh sales event for dashboard updates
        eventBus.emit(EVENTS.REFRESH_SALES);
      }

      resetForm();
      onSuccess();
      onDismiss();
    } catch (error) {
      console.error(
        isEditMode ? 'Error updating sale:' : 'Error creating sale:',
        error
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setErrors({});
    setActiveTab('direct');
    setSelectedSupplier(null);
    setShowInlineSupplierForm(false);
    setShowNewSupplierInput(false);
  };

  const handleClose = () => {
    resetForm();
    onDismiss();
  };

  const toggleTicketSelection = (ticketId: string) => {
    const ticket = availableTickets.find((t) => (t.id || t._id) === ticketId);

    setFormData((prev) => {
      const isSelecting = !prev.selectedTickets.includes(ticketId);
      // Only allow one ticket selection at a time for inventory sales
      const newSelectedTickets = isSelecting
        ? [ticketId] // Replace any existing selection
        : prev.selectedTickets.filter((id) => id !== ticketId);

      // Auto-fill details from the selected ticket
      if (isSelecting && ticket) {
        // Map category/block from ticket
        let categoryId = '';
        let blockId = '';

        if (ticket.category && categories.length > 0) {
          const matchingCategory = categories.find(
            (c) =>
              c.name.trim().toLowerCase() ===
              ticket.category?.trim().toLowerCase()
          );
          categoryId = matchingCategory ? matchingCategory.id : ticket.category;
        }

        if (ticket.block && blocks.length > 0) {
          const matchingBlock = blocks.find(
            (b) =>
              b.name.trim().toLowerCase() === ticket.block?.trim().toLowerCase()
          );
          blockId = matchingBlock ? matchingBlock.id : ticket.block;
        }

        return {
          ...prev,
          selectedTickets: newSelectedTickets,
          category: categoryId || ticket.category || prev.category,
          block: blockId || ticket.block || '',
          row: ticket.row || prev.row,
          seat: ticket.seat || prev.seat,
          restrictions: ticket.restrictions
            ? ticket.restrictions.split(',').map((r) => r.trim())
            : prev.restrictions,
          splitType: ticket.split || prev.splitType,
          currency: ticket.currency || prev.currency,
        };
      } else if (!isSelecting && newSelectedTickets.length === 0) {
        // Clear details when no tickets selected
        return {
          ...prev,
          selectedTickets: newSelectedTickets,
          category: '',
          block: '',
          row: '',
          seat: '',
          restrictions: [],
          splitType: '',
        };
      }

      return {
        ...prev,
        selectedTickets: newSelectedTickets,
      };
    });
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return cashOutline;
      case 'card':
        return cardOutline;
      case 'bankTransfer':
        return swapHorizontalOutline;
      default:
        return ellipsisHorizontalOutline;
    }
  };

  const getCategoryBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'vip':
        return 'warning';
      case 'premium':
        return 'tertiary';
      default:
        return 'primary';
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      className="add-sale-modal"
    >
      <IonHeader>
        <IonToolbar className="border-b">
          <IonTitle className="text-xl font-bold">
            <IonIcon icon={pricetagOutline} className="mr-2 align-middle" />
            {isEditMode ? t('sales.editSale') : t('sales.recordSale')}
          </IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={handleClose} fill="clear" color="medium">
              <IonIcon icon={closeCircleOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              onClick={handleSubmit}
              disabled={submitting || !event || !isFormValid()}
              fill="solid"
              color="primary"
              className="mr-2"
            >
              {submitting ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon icon={checkmarkCircleOutline} slot="start" />
                  {isEditMode ? t('common.save') : t('common.add')}
                </>
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="bg-gray-50 dark:bg-gray-950">
        {event && (
          <div className="max-w-4xl mx-auto p-4">
            {/* Event Info Card */}
            <IonCard className="mb-6 shadow-lg border border-primary-200 dark:border-primary-800">
              <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <IonCardTitle className="text-xl font-bold text-primary-900 dark:text-primary-100">
                      {event.name}
                    </IonCardTitle>
                    <IonText className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">
                      <IonIcon
                        icon={locationOutline}
                        className="mr-1 align-middle"
                      />
                      {event.venue}
                    </IonText>
                  </div>
                  <IonChip color="primary" className="mt-1">
                    {format(new Date(event.date), 'MMM dd')}
                    {event.time && ` • ${event.time}`}
                  </IonChip>
                </div>
              </IonCardHeader>
            </IonCard>

            {/* Sale Type Selector */}
            <div className="mb-6">
              <IonSegment
                value={activeTab}
                onIonChange={(e) =>
                  setActiveTab(e.detail.value as 'tickets' | 'direct')
                }
                className="mb-4"
              >
                <IonSegmentButton value="direct">
                  <IonLabel>
                    <IonIcon icon={documentTextOutline} className="mb-1" />
                    <div className="text-xs mt-1">Direct Sale</div>
                  </IonLabel>
                </IonSegmentButton>
                <IonSegmentButton
                  value="tickets"
                  disabled={availableTickets.length === 0}
                >
                  <IonLabel>
                    <IonIcon icon={ticketOutline} className="mb-1" />
                    <div className="text-xs mt-1">From Inventory</div>
                    {availableTickets.length > 0 && (
                      <IonBadge color="primary" className="mt-1">
                        {availableTickets.length}
                      </IonBadge>
                    )}
                  </IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </div>

            <IonGrid className="p-0">
              <IonRow>
                {/* Left Column - Buyer & Payment */}
                <IonCol size="12" sizeMd="6" className="pr-0 md:pr-2">
                  {/* Sale Type Selection */}
                  <IonCard className="mb-4 shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon
                          icon={swapHorizontalOutline}
                          className="mr-2"
                        />
                        Sale Type
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Type
                          </IonLabel>
                          <IonSelect
                            value={formData.saleType}
                            onIonChange={(e) => {
                              setFormData({
                                ...formData,
                                saleType: e.detail.value,
                                supplierId: '',
                                newSupplierName: '',
                              });
                              setShowNewSupplierInput(false);
                            }}
                          >
                            <IonSelectOption value="private">
                              Private Sale
                            </IonSelectOption>
                            <IonSelectOption value="supplier">
                              Supplier
                            </IonSelectOption>
                          </IonSelect>
                        </IonItem>

                        {formData.saleType === 'supplier' && (
                          <>
                            <IonItem
                              lines="none"
                              className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                                errors.supplierId ? 'ion-invalid' : ''
                              }`}
                            >
                              <IonLabel
                                position="stacked"
                                className="text-sm font-medium mb-1"
                              >
                                Supplier <span className="text-red-500">*</span>
                              </IonLabel>
                              <IonSelect
                                value={formData.supplierId}
                                onIonChange={(e) => {
                                  const value = e.detail.value;
                                  if (value === 'new') {
                                    setShowInlineSupplierForm(true);
                                    setFormData({
                                      ...formData,
                                      supplierId: '',
                                    });
                                    setSelectedSupplier(null);
                                  } else {
                                    setFormData({
                                      ...formData,
                                      supplierId: value,
                                    });
                                    // Find and set selected supplier details
                                    const supplier = suppliers.find(
                                      (s) => (s._id || s.id) === value
                                    );
                                    setSelectedSupplier(supplier || null);
                                    setShowInlineSupplierForm(false);
                                  }
                                  setShowNewSupplierInput(false);
                                  // Clear error when user selects a supplier
                                  if (errors.supplierId) {
                                    setErrors({ ...errors, supplierId: '' });
                                  }
                                }}
                              >
                                <IonSelectOption value="">
                                  Select Supplier
                                </IonSelectOption>
                                {suppliers.map((supplier) => (
                                  <IonSelectOption
                                    key={supplier._id || supplier.id}
                                    value={supplier._id || supplier.id}
                                  >
                                    {supplier.name}
                                  </IonSelectOption>
                                ))}
                                <IonSelectOption value="new">
                                  + Add New Supplier
                                </IonSelectOption>
                              </IonSelect>
                            </IonItem>

                            {errors.supplierId && (
                              <IonText color="danger">
                                <p className="text-sm mt-1 ml-4">
                                  {errors.supplierId}
                                </p>
                              </IonText>
                            )}

                            {showInlineSupplierForm && (
                              <InlineSupplierForm
                                onCancel={() => {
                                  setShowInlineSupplierForm(false);
                                  setFormData({ ...formData, supplierId: '' });
                                }}
                                onSave={async (supplierData) => {
                                  try {
                                    setLoadingSupplier(true);
                                    // Create the supplier
                                    const newSupplier =
                                      await supplierService.create(
                                        supplierData
                                      );

                                    // Add to suppliers list
                                    setSuppliers([...suppliers, newSupplier]);

                                    // Select the new supplier
                                    setFormData({
                                      ...formData,
                                      supplierId:
                                        newSupplier._id || newSupplier.id,
                                    });
                                    setSelectedSupplier(newSupplier);

                                    // Hide the form
                                    setShowInlineSupplierForm(false);
                                  } catch (error) {
                                    console.error(
                                      'Error creating supplier:',
                                      error
                                    );
                                  } finally {
                                    setLoadingSupplier(false);
                                  }
                                }}
                              />
                            )}

                            {selectedSupplier && !showInlineSupplierForm && (
                              <div className="mt-4">
                                <SupplierDetailsCard
                                  supplier={selectedSupplier}
                                  loading={loadingSupplier}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </IonList>
                    </IonCardContent>
                  </IonCard>

                  {/* Buyer Information */}
                  {formData.saleType !== 'supplier' && (
                    <IonCard className="mb-4 shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center">
                          <IonIcon icon={personOutline} className="mr-2" />
                          {t('sales.buyerInfo')}
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <IonList className="space-y-3">
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerName ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              {t('sales.buyerName')}
                            </IonLabel>
                            <IonInput
                              value={formData.buyerName}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerName: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerName) {
                                  setErrors({ ...errors, buyerName: '' });
                                }
                              }}
                              placeholder="John Doe"
                              className="text-base"
                            />
                            {errors.buyerName && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerName}
                              </IonNote>
                            )}
                          </IonItem>
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerEmail ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonIcon
                              icon={mailOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              {t('sales.buyerEmail')}
                            </IonLabel>
                            <IonInput
                              type="email"
                              value={formData.buyerEmail}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerEmail: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerEmail) {
                                  setErrors({ ...errors, buyerEmail: '' });
                                }
                              }}
                              placeholder="john.doe@example.com"
                              className="text-base"
                            />
                            {errors.buyerEmail && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerEmail}
                              </IonNote>
                            )}
                          </IonItem>
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerPhone ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonIcon
                              icon={callOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              {t('sales.buyerPhone')}
                            </IonLabel>
                            <IonInput
                              type="tel"
                              value={formData.buyerPhone}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerPhone: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerPhone) {
                                  setErrors({ ...errors, buyerPhone: '' });
                                }
                              }}
                              placeholder="+44 7000 000000"
                              className="text-base"
                            />
                            {errors.buyerPhone && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerPhone}
                              </IonNote>
                            )}
                          </IonItem>
                        </IonList>
                      </IonCardContent>
                    </IonCard>
                  )}
                  {/* Payment Details */}
                  <IonCard className="mb-4 shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon icon={walletOutline} className="mr-2" />
                        {t('sales.paymentInfo')}
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        {/* Currency Selection */}
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonIcon
                            icon={cashOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Currency
                          </IonLabel>
                          <IonSelect
                            value={formData.currency}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                currency: e.detail.value!,
                              })
                            }
                            interface="popover"
                            className="text-base"
                          >
                            {CURRENCIES.map((currency) => (
                              <IonSelectOption
                                key={currency.code}
                                value={currency.code}
                              >
                                {currency.symbol} - {currency.name}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </IonItem>
                        <IonItem
                          lines="none"
                          className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                            errors.salePrice ? 'ion-invalid' : ''
                          }`}
                        >
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            {activeTab === 'tickets'
                              ? t('sales.salePricePerTicket')
                              : t('sales.salePrice')}{' '}
                            <span className="text-red-500">*</span>
                          </IonLabel>
                          <IonInput
                            type="number"
                            value={formData.salePrice}
                            onIonInput={(e: any) => {
                              const value = e.target.value;
                              // Allow only numbers and decimal point
                              const numericValue = value.replace(
                                /[^0-9.]/g,
                                ''
                              );
                              // Ensure only one decimal point
                              const parts = numericValue.split('.');
                              const formattedValue =
                                parts.length > 2
                                  ? parts[0] + '.' + parts.slice(1).join('')
                                  : numericValue;
                              // Update the input value
                              e.target.value = formattedValue;
                              // Update form data
                              const price = parseFloat(formattedValue) || 0;
                              setFormData({
                                ...formData,
                                salePrice: price >= 0 ? price : 0,
                              });
                              // Clear error when user starts typing
                              if (errors.salePrice) {
                                setErrors({ ...errors, salePrice: '' });
                              }
                            }}
                            min="0"
                            step="0.01"
                            placeholder={`${getCurrencySymbol(
                              formData.currency
                            )}0.00`}
                            className="text-base font-semibold"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                          />
                          <IonNote slot="helper">
                            Total: {getCurrencySymbol(formData.currency)}
                            {(
                              (formData.salePrice || 0) *
                              (activeTab === 'direct'
                                ? formData.quantity
                                : formData.selectedTickets.length || 1)
                            ).toFixed(2)}
                          </IonNote>
                          {errors.salePrice && (
                            <IonNote
                              slot="error"
                              className="text-red-500 text-xs mt-1"
                            >
                              {errors.salePrice}
                            </IonNote>
                          )}
                        </IonItem>
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonLabel className="text-sm font-medium mb-2">
                            {t('sales.paymentMethod')}
                          </IonLabel>
                          <IonSelect
                            value={formData.paymentMethod}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                paymentMethod: e.detail.value,
                              })
                            }
                            interface="popover"
                            className="text-base"
                          >
                            <IonSelectOption value="cash">
                              <IonIcon icon={cashOutline} className="mr-2" />
                              {t('sales.cash')}
                            </IonSelectOption>
                            <IonSelectOption value="card">
                              <IonIcon icon={cardOutline} className="mr-2" />
                              {t('sales.card')}
                            </IonSelectOption>
                            <IonSelectOption value="bankTransfer">
                              <IonIcon
                                icon={swapHorizontalOutline}
                                className="mr-2"
                              />
                              {t('sales.transfer')}
                            </IonSelectOption>
                            <IonSelectOption value="other">
                              <IonIcon
                                icon={ellipsisHorizontalOutline}
                                className="mr-2"
                              />
                              {t('sales.other')}
                            </IonSelectOption>
                          </IonSelect>
                        </IonItem>
                      </IonList>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* Right Column - Location & Tickets/Notes */}
                <IonCol size="12" sizeMd="6" className="pl-0 md:pl-2">
                  {/* Location Details - Only show in direct sale mode */}
                  {activeTab === 'direct' && (
                    <IonCard className="mb-4 shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center justify-between">
                          <span className="flex items-center">
                            <IonIcon icon={locationOutline} className="mr-2" />
                            {t('inventory.location')}
                          </span>
                          {loadingStadium && (
                            <IonSpinner name="dots" className="h-5 w-5" />
                          )}
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <IonList className="space-y-3">
                          {/* Category Selection */}
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.category ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonIcon
                              icon={layersOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Category <span className="text-red-500">*</span>
                              {!loadingStadium && categories.length > 0 && (
                                <IonBadge color="primary" className="ml-2">
                                  {categories.length} available
                                </IonBadge>
                              )}
                            </IonLabel>
                            {loadingStadium ? (
                              <div className="flex items-center justify-center py-2">
                                <IonSpinner name="dots" className="h-6 w-6" />
                              </div>
                            ) : categories.length > 0 ? (
                              <IonSelect
                                value={formData.category}
                                onIonChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    category: e.detail.value,
                                    block: '',
                                  });
                                  // Clear error when user selects
                                  if (errors.category) {
                                    setErrors({ ...errors, category: '' });
                                  }
                                }}
                                placeholder="Select category"
                                interface="popover"
                                className="text-base"
                                disabled={false}
                              >
                                {categories.map((category) => (
                                  <IonSelectOption
                                    key={category.id}
                                    value={category.id}
                                  >
                                    {category.name}
                                    {category.type && (
                                      <IonBadge
                                        color={getCategoryBadgeColor(
                                          category.type
                                        )}
                                        className="ml-2"
                                      ></IonBadge>
                                    )}
                                  </IonSelectOption>
                                ))}
                              </IonSelect>
                            ) : (
                              <IonInput
                                value={formData.category}
                                onIonChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    category: e.detail.value!,
                                    block: '',
                                  });
                                  // Clear error when user types
                                  if (errors.category) {
                                    setErrors({ ...errors, category: '' });
                                  }
                                }}
                                placeholder="e.g., Upper Bowl, VIP"
                                className="text-base"
                                disabled={false}
                              />
                            )}
                            {errors.category && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.category}
                              </IonNote>
                            )}
                          </IonItem>

                          {/* Block Selection */}
                          <IonItem
                            lines="none"
                            className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                          >
                            <IonIcon
                              icon={gridOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Block
                              {!loadingStadium &&
                                formData.category &&
                                filteredBlocks.length > 0 && (
                                  <IonBadge color="secondary" className="ml-2">
                                    {filteredBlocks.length} in category
                                  </IonBadge>
                                )}
                            </IonLabel>
                            {loadingStadium ? (
                              <div className="flex items-center justify-center py-2">
                                <IonSpinner name="dots" className="h-6 w-6" />
                              </div>
                            ) : blocks.length > 0 ? (
                              <IonSelect
                                key={`block-select-${formData.category}`}
                                value={formData.block}
                                onIonChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    block: e.detail.value,
                                  });
                                }}
                                placeholder="Select block (optional)"
                                disabled={
                                  !formData.category &&
                                  filteredBlocks.length !== blocks.length
                                }
                                interface="popover"
                                className="text-base"
                              >
                                <IonSelectOption value="">
                                  No Block Selected
                                </IonSelectOption>
                                {filteredBlocks.map((block) => (
                                  <IonSelectOption
                                    key={block.id}
                                    value={block.id}
                                  >
                                    {block.name}
                                  </IonSelectOption>
                                ))}
                              </IonSelect>
                            ) : (
                              <IonInput
                                value={formData.block}
                                onIonChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    block: e.detail.value!,
                                  })
                                }
                                placeholder="e.g., Block A1, Section 123"
                                className="text-base"
                                disabled={false}
                              />
                            )}
                          </IonItem>

                          <IonGrid className="p-0">
                            <IonRow>
                              <IonCol size="6" className="pr-1">
                                <IonItem
                                  lines="none"
                                  className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                                >
                                  <IonLabel
                                    position="stacked"
                                    className="text-sm font-medium mb-1"
                                  >
                                    {t('inventory.row')}
                                  </IonLabel>
                                  <IonInput
                                    value={formData.row}
                                    onIonChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        row: e.detail.value!,
                                      })
                                    }
                                    placeholder="e.g., A, 12"
                                    className="text-base"
                                    disabled={false}
                                  />
                                </IonItem>
                              </IonCol>
                              <IonCol size="6" className="pl-1">
                                <IonItem
                                  lines="none"
                                  className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                                >
                                  <IonLabel
                                    position="stacked"
                                    className="text-sm font-medium mb-1"
                                  >
                                    Seat
                                  </IonLabel>
                                  <IonInput
                                    value={formData.seat}
                                    onIonChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        seat: e.detail.value!,
                                      })
                                    }
                                    placeholder="e.g., 14"
                                    className="text-base"
                                    disabled={false}
                                  />
                                </IonItem>
                              </IonCol>
                            </IonRow>
                          </IonGrid>

                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.splitType ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              {t('sales.split')}{' '}
                              <span className="text-red-500">*</span>
                            </IonLabel>
                            <IonSelect
                              value={formData.splitType}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  splitType: e.detail.value!,
                                });
                                if (errors.splitType) {
                                  setErrors({ ...errors, splitType: '' });
                                }
                              }}
                              interface="popover"
                              className="text-base"
                              disabled={false}
                            >
                              {SPLIT_TYPE_OPTIONS.map((option) => (
                                <IonSelectOption
                                  key={option.id}
                                  value={option.id}
                                >
                                  {option.label}
                                </IonSelectOption>
                              ))}
                            </IonSelect>
                            <IonNote slot="helper">
                              {formData.quantity > 0 && formData.splitType && (
                                <>
                                  <IonIcon
                                    icon={ticketOutline}
                                    className="mr-1"
                                  />
                                  {calculateSplitGroups(
                                    formData.quantity,
                                    formData.splitType
                                  )}
                                </>
                              )}
                            </IonNote>
                            {errors.splitType && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.splitType}
                              </IonNote>
                            )}
                          </IonItem>

                          {/* Quantity Field for Direct Sales */}
                          {activeTab === 'direct' && (
                            <IonItem
                              lines="none"
                              className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                                errors.quantity ? 'ion-invalid' : ''
                              }`}
                            >
                              <IonLabel
                                position="stacked"
                                className="text-sm font-medium mb-1"
                              >
                                {t('inventory.quantity')}{' '}
                                <span className="text-red-500">*</span>
                              </IonLabel>
                              <IonInput
                                type="number"
                                value={formData.quantity}
                                onIonChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    quantity: parseInt(e.detail.value!) || 1,
                                  })
                                }
                                min="1"
                                placeholder="Number of tickets"
                                className="text-base"
                                disabled={false}
                              />
                              {errors.quantity && (
                                <IonNote
                                  slot="error"
                                  className="text-red-500 text-xs mt-1"
                                >
                                  {errors.quantity}
                                </IonNote>
                              )}
                            </IonItem>
                          )}

                          {/* Restrictions Selection */}
                          <div className="ion-padding-vertical">
                            <IonLabel className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                              <IonIcon
                                icon={informationCircleOutline}
                                className="mr-1 align-middle"
                              />
                              Ticket Restrictions
                            </IonLabel>

                            <div className="space-y-2">
                              {RESTRICTION_OPTIONS.map((restriction) => (
                                <IonItem
                                  key={restriction.id}
                                  lines="none"
                                  className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2"
                                >
                                  <IonCheckbox
                                    checked={formData.restrictions.includes(
                                      restriction.id
                                    )}
                                    onIonChange={(e) => {
                                      const checked = e.detail.checked;
                                      setFormData({
                                        ...formData,
                                        restrictions: checked
                                          ? [
                                              ...formData.restrictions,
                                              restriction.id,
                                            ]
                                          : formData.restrictions.filter(
                                              (r) => r !== restriction.id
                                            ),
                                      });
                                    }}
                                    labelPlacement="end"
                                  >
                                    <IonLabel className="ml-2">
                                      <h3 className="text-sm font-medium">
                                        {restriction.label}
                                      </h3>
                                    </IonLabel>
                                  </IonCheckbox>
                                </IonItem>
                              ))}

                              {/* Custom Restriction Input */}
                              <IonItem
                                lines="none"
                                className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                              >
                                <IonLabel
                                  position="stacked"
                                  className="text-sm font-medium mb-1"
                                >
                                  Other Restriction
                                </IonLabel>
                                <div className="flex items-center gap-2">
                                  <IonInput
                                    value={formData.customRestriction}
                                    onIonChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        customRestriction: e.detail.value!,
                                      })
                                    }
                                    placeholder="Enter custom restriction"
                                    className="text-base flex-1"
                                    disabled={false}
                                  />
                                  <IonButton
                                    size="small"
                                    fill="outline"
                                    disabled={
                                      !formData.customRestriction.trim()
                                    }
                                    onClick={() => {
                                      if (formData.customRestriction.trim()) {
                                        setFormData({
                                          ...formData,
                                          restrictions: [
                                            ...formData.restrictions,
                                            formData.customRestriction.trim(),
                                          ],
                                          customRestriction: '',
                                        });
                                      }
                                    }}
                                  >
                                    Add
                                  </IonButton>
                                </div>
                              </IonItem>

                              {/* Display selected restrictions */}
                              {formData.restrictions.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {formData.restrictions.map((restriction) => {
                                    const predefined = RESTRICTION_OPTIONS.find(
                                      (r) => r.id === restriction
                                    );
                                    return (
                                      <IonChip
                                        key={restriction}
                                        color="secondary"
                                        onClick={() => {
                                          setFormData({
                                            ...formData,
                                            restrictions:
                                              formData.restrictions.filter(
                                                (r) => r !== restriction
                                              ),
                                          });
                                        }}
                                      >
                                        <IonLabel>
                                          {predefined
                                            ? predefined.label
                                            : restriction}
                                        </IonLabel>
                                        <IonIcon icon={closeCircle} />
                                      </IonChip>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </IonList>
                      </IonCardContent>
                    </IonCard>
                  )}

                  {/* Selected Ticket Details - Show when ticket is selected in inventory mode */}
                  {activeTab === 'tickets' &&
                    formData.selectedTickets.length > 0 &&
                    (() => {
                      const selectedTicket = availableTickets.find(
                        (t) => (t.id || t._id) === formData.selectedTickets[0]
                      );
                      if (!selectedTicket) return null;

                      return (
                        <IonCard className="mb-4 shadow-md border-2 border-primary-200 dark:border-primary-800">
                          <IonCardHeader className="pb-2 bg-primary-50 dark:bg-primary-900/30">
                            <IonCardTitle className="text-lg flex items-center">
                              <IonIcon icon={ticketOutline} className="mr-2" />
                              Selected Ticket Details
                            </IonCardTitle>
                          </IonCardHeader>
                          <IonCardContent>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Location:
                                </span>
                                <span className="font-medium">
                                  {selectedTicket.category}
                                  {selectedTicket.block &&
                                    ` - ${selectedTicket.block}`}
                                  {selectedTicket.row &&
                                    ` Row ${selectedTicket.row}`}
                                  {selectedTicket.seat &&
                                    ` Seat ${selectedTicket.seat}`}
                                </span>
                              </div>
                              {selectedTicket.split && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Split:
                                  </span>
                                  <span className="font-medium">
                                    {selectedTicket.split}
                                  </span>
                                </div>
                              )}
                              {selectedTicket.restrictions && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Restrictions:
                                  </span>
                                  <span className="font-medium text-orange-600">
                                    {selectedTicket.restrictions}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Purchase Price:
                                </span>
                                <span className="font-medium">
                                  {getCurrencySymbol(
                                    selectedTicket.currency || formData.currency
                                  )}
                                  {selectedTicket.purchasePrice.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Available:
                                </span>
                                <span className="font-medium">
                                  {selectedTicket.quantity -
                                    (selectedTicket.soldQuantity || 0)}{' '}
                                  of {selectedTicket.quantity}
                                </span>
                              </div>
                            </div>
                          </IonCardContent>
                        </IonCard>
                      );
                    })()}

                  {/* Ticket Selection or Notes */}
                  {activeTab === 'tickets' ? (
                    <IonCard className="shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center justify-between">
                          <span className="flex items-center">
                            <IonIcon icon={ticketOutline} className="mr-2" />
                            {t('sales.selectTickets')}
                          </span>
                          {formData.selectedTickets.length > 0 && (
                            <IonBadge color="primary">
                              {formData.selectedTickets.length} selected
                            </IonBadge>
                          )}
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        {errors.selectedTickets && (
                          <div className="mb-3">
                            <IonNote className="text-red-500">
                              {errors.selectedTickets}
                            </IonNote>
                          </div>
                        )}
                        {/* Show quantity selector when tickets are selected */}
                        {formData.selectedTickets.length > 0 &&
                          (() => {
                            const selectedTicket = availableTickets.find(
                              (t) =>
                                (t.id || t._id) === formData.selectedTickets[0]
                            );
                            const maxAvailable = selectedTicket
                              ? selectedTicket.quantity -
                                (selectedTicket.soldQuantity || 0)
                              : 1;

                            return (
                              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <IonLabel className="text-sm font-medium block mb-2">
                                  Total Quantity to Sell
                                </IonLabel>
                                <IonInput
                                  type="number"
                                  value={formData.quantity}
                                  onIonChange={(e) => {
                                    const value = parseInt(
                                      e.detail.value || '0'
                                    );
                                    setFormData({
                                      ...formData,
                                      quantity: value,
                                    });
                                  }}
                                  onIonBlur={() => {
                                    // Clamp the value when user finishes typing
                                    const clampedValue = Math.min(
                                      Math.max(1, formData.quantity),
                                      maxAvailable
                                    );
                                    if (clampedValue !== formData.quantity) {
                                      setFormData({
                                        ...formData,
                                        quantity: clampedValue,
                                      });
                                    }
                                  }}
                                  min="1"
                                  max={maxAvailable}
                                  className="bg-white dark:bg-gray-800 rounded p-2"
                                />
                                <IonNote className="text-xs mt-1">
                                  Selling {formData.quantity} of {maxAvailable}{' '}
                                  available tickets
                                </IonNote>
                              </div>
                            );
                          })()}
                        {loadingTickets ? (
                          <div className="flex justify-center py-8">
                            <IonSpinner />
                          </div>
                        ) : availableTickets.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <IonIcon
                              icon={informationCircleOutline}
                              className="text-4xl mb-2"
                            />
                            <p>{t('sales.noAvailableTickets')}</p>
                          </div>
                        ) : (
                          <IonList className="space-y-2 max-h-60 overflow-y-auto">
                            {availableTickets.map((ticket) => (
                              <IonItem
                                key={ticket.id || ticket._id}
                                className="rounded-lg bg-gray-50 dark:bg-gray-900"
                                lines="none"
                              >
                                <IonCheckbox
                                  slot="start"
                                  checked={formData.selectedTickets.includes(
                                    ticket.id || ticket._id || ''
                                  )}
                                  onIonChange={() =>
                                    toggleTicketSelection(
                                      ticket.id || ticket._id || ''
                                    )
                                  }
                                />
                                <IonLabel>
                                  <h3 className="font-medium">
                                    {ticket.category && `${ticket.category}`}
                                    {ticket.block && ` - ${ticket.block}`}
                                    {ticket.row && ` Row ${ticket.row}`}
                                    {ticket.seat && ` Seat ${ticket.seat}`}
                                  </h3>
                                  <p className="text-sm text-gray-500">
                                    Cost:{' '}
                                    {getCurrencySymbol(
                                      ticket.currency || formData.currency
                                    )}
                                    {ticket.purchasePrice} • Available:{' '}
                                    {ticket.quantity -
                                      (ticket.soldQuantity || 0)}{' '}
                                    of {ticket.quantity}
                                    {ticket.split &&
                                      ticket.split !== 'all-together' &&
                                      ` • Split: ${ticket.split}`}
                                  </p>
                                  {ticket.restrictions && (
                                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                      {ticket.restrictions}
                                    </p>
                                  )}
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        )}
                      </IonCardContent>
                    </IonCard>
                  ) : (
                    <IonCard className="shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center">
                          <IonIcon
                            icon={documentTextOutline}
                            className="mr-2"
                          />
                          {t('sales.notes')}
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <IonTextarea
                          value={formData.notes}
                          onIonChange={(e) =>
                            setFormData({ ...formData, notes: e.detail.value! })
                          }
                          placeholder={t('sales.notesPlaceholder')}
                          className="text-base bg-gray-50 dark:bg-gray-900 rounded-lg p-3"
                          rows={5}
                        />
                      </IonCardContent>
                    </IonCard>
                  )}
                </IonCol>
              </IonRow>
            </IonGrid>

            {/* Profit Summary */}
            {activeTab === 'tickets' &&
              formData.selectedTickets.length > 0 &&
              formData.salePrice > 0 && (
                <IonCard className="mt-4 shadow-lg border-2 border-green-200 dark:border-green-800">
                  <IonCardContent className="p-4">
                    <IonGrid>
                      <IonRow>
                        <IonCol size="4" className="text-center">
                          <IonText className="text-sm text-gray-600 dark:text-gray-400">
                            {t('sales.totalCost')}
                          </IonText>
                          <IonText className="block text-xl font-bold mt-1">
                            {getCurrencySymbol(formData.currency)}
                            {calculateTotalCost().toFixed(2)}
                          </IonText>
                        </IonCol>
                        <IonCol size="4" className="text-center">
                          <IonText className="text-sm text-gray-600 dark:text-gray-400">
                            {t('sales.salePrice')}
                          </IonText>
                          <IonText className="block text-xl font-bold mt-1">
                            {getCurrencySymbol(formData.currency)}
                            {(formData.salePrice * formData.quantity).toFixed(
                              2
                            )}
                          </IonText>
                        </IonCol>
                        <IonCol size="4" className="text-center">
                          <IonText className="text-sm text-gray-600 dark:text-gray-400">
                            {t('sales.profit')}
                          </IonText>
                          <IonText
                            className={`block text-xl font-bold mt-1 ${
                              calculateProfit() >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {getCurrencySymbol(formData.currency)}
                            {calculateProfit().toFixed(2)}
                          </IonText>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCardContent>
                </IonCard>
              )}
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default AddSaleModal;
